import {
  type ChildAgeGenderCriterionFailure,
  isChildAgeGenderCriterion,
  NonQuotaTargeting,
  outboundChildAgeGenderCriterion,
  Quota,
  QuotaCriteria,
} from '@ps/survey-utils';
import { Result } from '@sapphire/result';

import { FeasibilityDefaultQuotaCriteriaDto } from '../dtos/feasibility.dto';

import {
  FeasibilityQuotaRows,
  TargetedQuota,
  toQuotaSet,
  withoutZipcodeConditions,
  withResolvedUnitsForPayload,
} from '../domain';

import { FeasibilityDefaultQuotaPayloadMapper } from './feasibility-default-quota.payload.mapper';
import { FeasibilityQuotaCriteriaPayloadMapper } from './feasibility-quota-criteria.payload.mapper';

export interface FeasibilityQuotaPayloadResult extends FeasibilityQuotaRows {
  readonly defaultQuotaCriteria: FeasibilityDefaultQuotaCriteriaDto[];
}

/**
 * Survey quotas + the default criteria bucket -> the API's `quotas` and
 * `defaultQuota` criteria, plus the drawer-facing `quotaSet`.
 */
export class FeasibilityQuotaPayloadMapper {
  constructor(
    private readonly _quotas: readonly Quota[],
    private readonly _defaultQuotaCriteria: readonly FeasibilityDefaultQuotaCriteriaDto[],
    private readonly _completesRequired: number,
  ) {}

  public map(): FeasibilityQuotaPayloadResult {
    const targetedQuotas = this._targetedQuotas();
    const targeting = NonQuotaTargeting.from(
      targetedQuotas.map((quota) => quota.qualificationCodes()),
    );
    const defaultCriteria = this._defaultQuotaCriteria.map((criterion) =>
      withResolvedUnitsForPayload(criterion),
    );
    const criteriaMapper = new FeasibilityQuotaCriteriaPayloadMapper(
      defaultCriteria,
      targeting,
    );

    return {
      ...(targetedQuotas.length
        ? this._rowsFromQuotas(targetedQuotas, criteriaMapper)
        : new FeasibilityDefaultQuotaPayloadMapper(
            defaultCriteria,
            criteriaMapper,
            this._completesRequired,
          ).map()),
      defaultQuotaCriteria: defaultCriteria.map((criterion) =>
        this._taggedDefaultCriterion(
          withoutZipcodeConditions(criterion),
          targeting,
        ),
      ),
    };
  }

  private _targetedQuotas(): readonly TargetedQuota[] {
    return this._quotas
      .flatMap((quota) => this._childAgeGenderConverted(quota))
      .flatMap((quota) =>
        TargetedQuota.from(quota).mapOr([], (targeted) => [targeted]),
      );
  }

  // Each children (218) criterion is converted to its real 232 wire shape, same as
  // SurveyQuotaPayloadMapper does for the survey-save payload — feasibility must see the same
  // age/gender-expanded condition codes the backend persists. A quota with an incomplete children
  // criterion (no gender/range chosen yet) is dropped.
  private _childAgeGenderConverted(quota: Quota): readonly Quota[] {
    return Result.all(
      quota.criteria.map((criterion) =>
        this._childAgeGenderConvertedCriterion(criterion),
      ),
    ).match({
      ok: (criteria) => [{ ...quota, criteria }],
      err: () => [],
    });
  }

  private _childAgeGenderConvertedCriterion(
    criterion: QuotaCriteria,
  ): Result<QuotaCriteria, ChildAgeGenderCriterionFailure> {
    if (!isChildAgeGenderCriterion(criterion)) {
      return Result.ok(criterion);
    }
    return outboundChildAgeGenderCriterion(criterion).map(
      ({ criterion: outboundCriterion }) => outboundCriterion,
    );
  }

  private _rowsFromQuotas(
    targetedQuotas: readonly TargetedQuota[],
    criteriaMapper: FeasibilityQuotaCriteriaPayloadMapper,
  ): FeasibilityQuotaRows {
    return {
      quotas: targetedQuotas.map((quota) =>
        quota.toPayloadRow(criteriaMapper.mapForQuota(quota.ownCriteria())),
      ),
      quotaSet: toQuotaSet(targetedQuotas),
    };
  }

  /** None means the criterion states no verdict, so only the key is omitted — not the criterion. */
  private _taggedDefaultCriterion(
    criterion: FeasibilityDefaultQuotaCriteriaDto,
    targeting: NonQuotaTargeting,
  ): FeasibilityDefaultQuotaCriteriaDto {
    return targeting
      .flagForDefaultCriterion(criterion.qualification_code)
      .mapOr(criterion, (nonQuotaTargeted) => ({
        ...criterion,
        non_quota_targeted: nonQuotaTargeted,
      }));
  }
}
